import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, ImproverAngularEnvironment } from '@improver/app-config';
import * as Sentry from '@sentry/angular-ivy';

import { Observable, of } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { SentryConfiguration } from './sentry.model';

const init = ({ environment, release, dsn, apiUrl }: SentryConfiguration) =>
  Sentry.init({
    environment,
    release,
    dsn,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.routingInstrumentation,
      }),
      new Sentry.Replay(),
    ],
    tracePropagationTargets: ['localhost', apiUrl],
    tracesSampleRate: 1,
    replaysOnErrorSampleRate: 1,
    ignoreErrors: [
      'Server returned code 403 with body',
      'Server returned code 502 with body',
      'ChunkLoadError',
      'Server returned code 400 with body',
      'Http failure response',
    ],
    ignoreTransactions: [/\/login_with_token\//],
  });

@Injectable()
export class SentryConfigurationService {
  private config: Partial<SentryConfiguration> = {};
  constructor(
    @Inject(APP_CONFIG) private appConfig: ImproverAngularEnvironment,
    private readonly httpClient: HttpClient
  ) {}

  initialize(): Observable<Partial<SentryConfiguration>> {
    if (!this.appConfig.production) {
      this.config = {};

      return of({ ...this.config });
    }

    return this.httpClient.get<SentryConfiguration>('/assets/configs/env.config.json').pipe(
      filter(Boolean),
      tap((config: SentryConfiguration) => {
        this.config = { ...config };

        if (this.eligibleForRemoteTracking()) {
          init(this.config as SentryConfiguration);

          return of({ ...this.config });
        }
      }),
      catchError(() => {
        this.config = {};

        return of({ ...this.config });
      })
    );
  }

  eligibleForRemoteTracking(): boolean {
    return this.config.environment === 'QA';
  }
}
